import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClickOutside } from 'qampo-ui-library';
import type { RoomProperty } from '../../store/roomProperties/roomPropertiesSlice';
import SearchIcon from '../../assets/icons/search.svg?react';
import styles from './PatientDetailsCard.module.css';

interface RoomNeedPickerProps {
  roomNeeds: RoomProperty[];
  allRoomProperties: RoomProperty[];
  onChange: (roomNeeds: RoomProperty[]) => void;
}

export const RoomNeedPicker = ({
  roomNeeds,
  allRoomProperties,
  onChange
}: RoomNeedPickerProps) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [showResults, setShowResults] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useClickOutside(wrapperRef, () => setShowResults(false));

  const results = useMemo(() => {
    const selectedIds = new Set(roomNeeds.map((rp) => rp.id));
    const filtered = allRoomProperties.filter((rp) => !selectedIds.has(rp.id));
    if (query.length === 0) return filtered;
    const lowerQuery = query.toLowerCase();
    return filtered.filter((rp) => rp.name.toLowerCase().includes(lowerQuery));
  }, [query, allRoomProperties, roomNeeds]);

  const handleSelect = useCallback(
    (rp: RoomProperty) => {
      onChange([...roomNeeds, rp]);
      setQuery('');
      setShowResults(false);
    },
    [roomNeeds, onChange]
  );

  const handleRemove = useCallback(
    (id: string) => {
      onChange(roomNeeds.filter((r) => r.id !== id));
    },
    [roomNeeds, onChange]
  );

  return (
    <div ref={wrapperRef} className={styles.roomNeedWrapper}>
      <div className={styles.roomNeedInput}>
        <SearchIcon className={styles.searchIcon} />
        <div className={styles.pills}>
          {roomNeeds.map((rp) => (
            <span key={rp.id} className={styles.pill}>
              {rp.name}
              <button
                className={styles.pillRemove}
                onClick={() => handleRemove(rp.id)}
                type="button"
              >
                &times;
              </button>
            </span>
          ))}
        </div>
        <input
          className={styles.searchField}
          type="text"
          placeholder={roomNeeds.length === 0 ? t('room-need-placeholder') : ''}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setShowResults(true);
          }}
          onFocus={() => setShowResults(true)}
        />
      </div>
      {showResults && results.length > 0 && (
        <div className={styles.roomNeedDropdown}>
          {results.map((rp) => (
            <button
              key={rp.id}
              className={styles.roomNeedItem}
              onClick={() => handleSelect(rp)}
              type="button"
            >
              {rp.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
